/**
 * Shared parsing for the comma-separated `paths` assignment field.
 *
 * Some PLP-targeting documents (the PLP hero, the redesigned product takeover
 * tile) are assigned to their PLPs by a text field of category paths rather
 * than by a content relationship to the collection. Both read that field the
 * same way, and so does the revalidation webhook when working out which PLPs a
 * document edit invalidates — keeping the logic here means an editor's
 * assignment resolves identically on render and on revalidation.
 */

/** Delimiter used in the Assignment `paths` field to list multiple PLP paths. */
const PLP_PATH_DELIMITER = ',';

/**
 * Locale route folders that prefix a PLP URL (e.g. /products/cycling) but are
 * not part of the internal category path the fetch matches against (/cycling).
 * Stripping them lets editors paste the browser path as-is.
 */
const PLP_PAGE_PATH_PREFIXES = ['products', 'produits'];

/**
 * Normalizes a category path for comparison: trims, lowercases, strips
 * surrounding slashes, and drops a leading `products/`/`produits/` route folder
 * so both `products/cycling` and `cycling` resolve to the same value.
 */
export const normalizePlpAssignmentPath = (value: string): string => {
  const trimmed = value
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');
  const [first, ...rest] = trimmed.split('/');

  return PLP_PAGE_PATH_PREFIXES.includes(first) && rest.length > 0
    ? rest.join('/')
    : trimmed;
};

/** The normalized, de-duplicated category paths an assignment field points at. */
export const parsePlpAssignmentPaths = (
  paths: string | null | undefined
): string[] => [
  ...new Set(
    (paths ?? '')
      .split(PLP_PATH_DELIMITER)
      .map(normalizePlpAssignmentPath)
      .filter(Boolean)
  ),
];

/** The path with any query string removed, e.g. `cycling?brand=mec` -> `cycling`. */
const withoutQuery = (path: string): string => path.split('?')[0];

/**
 * True when a comma-separated `paths` assignment field contains the given
 * category path. Mirrors the PDP deals banner comma-separated convention.
 *
 * The match is exact, query string included. The category path a PLP fetch is
 * keyed by keeps the query string when merch has a collection for that filter
 * (see `preparePathForCache`, e.g. Kids Age on kids-baby), and a category-level
 * hero or cross-linking band belongs on the unfiltered page only — the same
 * reason client-side refinement hides them. A filtered view gets one only when
 * it is assigned that filtered path.
 */
export const plpAssignmentMatchesPath = (
  paths: string | null | undefined,
  categoryPath: string
): boolean => {
  const target = normalizePlpAssignmentPath(categoryPath ?? '');

  return !!target && parsePlpAssignmentPaths(paths).includes(target);
};

/**
 * {@link plpAssignmentMatchesPath}, but a base-path assignment also claims every
 * filtered view of that PLP (`camping` matches `camping?brand=yeti`). For deals
 * strips, which merch wants on the filtered views of the PLP they are assigned
 * to (ED-205).
 */
export const plpAssignmentMatchesPathOrFilteredView = (
  paths: string | null | undefined,
  categoryPath: string
): boolean =>
  plpAssignmentMatchesPath(paths, categoryPath) ||
  plpAssignmentMatchesPath(paths, withoutQuery(categoryPath ?? ''));
